import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CategoryFilterMode } from '@sleed/bizcommerce-api';
import type { FilterTemplateValueToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Field } from '~/components/promotion/editor-section';
import { serverMessage } from '~/lib/api/server-error';
import {
	applySettings,
	isCategoryFilter,
	showsCategoryLevel,
	showsNestingDepth,
	tokenToSettings,
	TOOLTIP_MAX,
	type CategoryFilterModeValue,
	type FilterTemplateValueSettings
} from '~/lib/schemas/filter-template-value';

const DEFAULT_MODE = '__default__';

interface Props {
	row: FilterTemplateValueToken | null;
	onOpenChange: (open: boolean) => void;
	onSave: (token: FilterTemplateValueToken) => Promise<unknown>;
	saving?: boolean;
}

function toNullableInt(raw: string): number | null {
	const v = raw.trim();
	if (v === '') return null;
	const n = Number(v);
	return Number.isInteger(n) ? n : null;
}

/** Tooltip + (Category filters only) category display configuration of one template filter row. */
export function FilterValueSettingsDialog({ row, onOpenChange, onSave, saving }: Props) {
	const { t } = useTranslation();
	const [settings, setSettings] = useState<FilterTemplateValueSettings | null>(null);

	useEffect(() => {
		setSettings(row ? tokenToSettings(row) : null);
	}, [row]);

	const set = <K extends keyof FilterTemplateValueSettings>(key: K, value: FilterTemplateValueSettings[K]) =>
		setSettings(prev => (prev ? { ...prev, [key]: value } : prev));

	const submit = async () => {
		if (!row || !settings) return;
		try {
			await onSave(applySettings(row, settings));
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			toast.error(serverMessage(err));
		}
	};

	const mode = settings?.categoryFilterMode ?? null;

	return (
		<Dialog open={Boolean(row)} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{t('catalog.filterTemplates.settings.title')}</DialogTitle>
					<DialogDescription>{row?.FilterName || ''}</DialogDescription>
				</DialogHeader>
				{settings && row && (
					<div className="flex flex-col gap-4">
						<Field label={t('catalog.filterTemplates.settings.toolTip')}>
							<Input
								value={settings.toolTip}
								maxLength={TOOLTIP_MAX}
								onChange={e => set('toolTip', e.target.value)}
								placeholder={t('catalog.filterTemplates.settings.toolTipPlaceholder')}
							/>
						</Field>

						{isCategoryFilter(row) && (
							<>
								<Field label={t('catalog.filterTemplates.settings.categoryFilterMode')}>
									<Select
										value={mode == null ? DEFAULT_MODE : String(mode)}
										onValueChange={v => {
											const next = (v === DEFAULT_MODE ? null : Number(v)) as CategoryFilterModeValue;
											setSettings(prev => (prev ? { ...prev, categoryFilterMode: next } : prev));
										}}>
										<SelectTrigger className="w-full">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value={DEFAULT_MODE}>{t('catalog.filterTemplates.settings.modes.default')}</SelectItem>
											<SelectItem value={String(CategoryFilterMode.Subcategories)}>
												{t('catalog.filterTemplates.settings.modes.subcategories')}
											</SelectItem>
											<SelectItem value={String(CategoryFilterMode.Siblings)}>
												{t('catalog.filterTemplates.settings.modes.siblings')}
											</SelectItem>
											<SelectItem value={String(CategoryFilterMode.ByLevel)}>
												{t('catalog.filterTemplates.settings.modes.byLevel')}
											</SelectItem>
										</SelectContent>
									</Select>
								</Field>
								{showsNestingDepth(mode) && (
									<Field
										label={t('catalog.filterTemplates.settings.categoryNestingDepth')}
										help={t('catalog.filterTemplates.settings.categoryNestingDepthHelp')}>
										<Input
											type="number"
											step={1}
											min={-1}
											value={settings.categoryNestingDepth ?? ''}
											onChange={e => set('categoryNestingDepth', toNullableInt(e.target.value))}
										/>
									</Field>
								)}
								{showsCategoryLevel(mode) && (
									<Field
										label={t('catalog.filterTemplates.settings.categoryLevel')}
										help={t('catalog.filterTemplates.settings.categoryLevelHelp')}>
										<Input
											type="number"
											step={1}
											min={-1}
											value={settings.categoryLevel ?? ''}
											onChange={e => set('categoryLevel', toNullableInt(e.target.value))}
										/>
									</Field>
								)}
							</>
						)}
					</div>
				)}
				<DialogFooter>
					<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
						{t('common.cancel')}
					</Button>
					<Button type="button" onClick={() => void submit()} disabled={saving || !settings}>
						{saving ? t('common.saving') : t('common.save')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
